import type { AliasListParams } from './aliases';
import type {
  ForwardingLogParams,
  SenderBlockListParams,
  SenderListParams,
} from './forwarding';
import type { LoginActivityParams } from './account';

// ---------------------------------------------------------------------------
// Typed react-query key factory.
//
// Every key is a readonly tuple so invalidation can target a whole domain
// (e.g. `queryKeys.aliases.all`) or a single parameterized list
// (e.g. `queryKeys.aliases.list(params)`). Filters are folded into the key so
// distinct filter sets cache independently.
// ---------------------------------------------------------------------------
export const queryKeys = {
  aliases: {
    all: ['aliases'] as const,
    lists: () => [...queryKeys.aliases.all, 'list'] as const,
    list: (params: AliasListParams = {}) =>
      [...queryKeys.aliases.lists(), params] as const,
    detail: (id: string) => [...queryKeys.aliases.all, 'detail', id] as const,
    domains: () => [...queryKeys.aliases.all, 'domains'] as const,
  },

  forwarding: {
    all: ['forwarding'] as const,
    logLists: () => [...queryKeys.forwarding.all, 'logs'] as const,
    logs: (params: ForwardingLogParams = {}) =>
      [...queryKeys.forwarding.logLists(), params] as const,
    senderLists: () => [...queryKeys.forwarding.all, 'senders'] as const,
    senders: (params: SenderListParams = {}) =>
      [...queryKeys.forwarding.senderLists(), params] as const,
    senderBlockLists: () => [...queryKeys.forwarding.all, 'sender-blocks'] as const,
    senderBlocks: (params: SenderBlockListParams = {}) =>
      [...queryKeys.forwarding.senderBlockLists(), params] as const,
  },

  billing: {
    all: ['billing'] as const,
    plans: () => [...queryKeys.billing.all, 'plans'] as const,
    subscription: () => [...queryKeys.billing.all, 'subscription'] as const,
    invoices: () => [...queryKeys.billing.all, 'invoices'] as const,
  },

  dashboard: {
    all: ['dashboard'] as const,
    summary: () => [...queryKeys.dashboard.all, 'summary'] as const,
  },

  account: {
    all: ['account'] as const,
    me: () => [...queryKeys.account.all, 'me'] as const,
    loginActivityLists: () => [...queryKeys.account.all, 'login-activity'] as const,
    loginActivity: (params: LoginActivityParams = {}) =>
      [...queryKeys.account.loginActivityLists(), params] as const,
  },
} as const;
